// Components==============
import updateExerciseStatus from 'actions/authentication/updateExerciseStatus';
import storeWrongAnswer from 'actions/wrongAnswer/store';
import ControlBar from 'components/Layout/ControlBar/ControlBar';
import ExerciseBottom from 'components/UI/ExerciseBottom/ExerciseBottom';
import ExerciseWrapper from 'components/UI/ExerciseWrapper/ExerciseWrapper';
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { HomeworkContext } from 'screens/Homework/Homework';
import { useAudio } from 'utilities/useAudio';
import { useHomeworkNavigation } from 'utilities/useHomeworkNavigation';
import Card from './Card/Card';
import './HighlightExercise.module.scss';
// =========================

export const highlightChars = [
    '|' as const,
    '#' as const,
    '^' as const,
    '~' as const,
    '_' as const,
    '{' as const,
    '}' as const,
    '[' as const,
];

type HighlightSelectType = {
    id: string;
    index: number;
    wordIndex: number;
    type: 'sentences' | 'word' | 'sentence';
};

type AnsweredType = {
    id: string;
    paragraphId: string;
    words: number[];
    status: 'answered' | 'correct';
};

type SentenceType = {
    id: string;
    sentence: string;
};

type HighlightExerciseContextType = {
    sentences: SentenceType[];
    selected: HighlightSelectType;
    setSelected: React.Dispatch<React.SetStateAction<HighlightSelectType>>;
    answeredSentences: AnsweredType[];
    setAnsweredSentences: React.Dispatch<React.SetStateAction<AnsweredType[]>>;
    paragraphId: string;
    setTextToBeRead: React.Dispatch<React.SetStateAction<string>>;
    readSentenceSequence: number;
    lastListenedIndex: number;
};

export const HighlightExerciseContext = createContext(
    {} as HighlightExerciseContextType
);

export default function HighlightExercise({
    exercise,
    setStatus,
}: {
    exercise: ExerciseEntity;
    setStatus: React.Dispatch<React.SetStateAction<exerciseStatusType>>;
}) {
    const { setProgress, sequence } = useContext(HomeworkContext);

    const [exerciseStatus, setExerciseStatus] = useState<exerciseStatusType>(
        'started'
    );
    const [selected, setSelected] = useState<HighlightSelectType>({
        id: '',
        index: -1,
        wordIndex: -1,
        type: 'sentences',
    });

    const [answeredSentences, setAnsweredSentences] = useState<AnsweredType[]>(
        []
    );
    const [textToBeRead, setTextToBeRead] = useState('');
    const [readSentenceSequence, setReadSentenceSequence] = useState(-1);
    const [lastListenedIndex, setLastListenedIndex] = useState(0);

    const { homeworkId, treatmentId } = useParams() as {
        homeworkId: string;
        treatmentId: string;
    };

    const paragraphs = exercise.highlight_data;

    const dispatch = useDispatch();

    const { next, current } = useHomeworkNavigation([], true);

    const { play, isPlaying, stop, url } = useAudio({
        text: textToBeRead,
    });

    const storedAnsweredSentences = useSelector((state: ReduxStore) =>
        state.authentication?.user?.exercise_status.find(
            (es) => es.exercise_id === exercise.id
        )
    )?.metadata;

    const sentences = paragraphs[current].attributes.sentences.map((s) => ({
        id: s.key,
        sentence: s.attributes.text,
    }));

    const paragraphId = paragraphs[current].key;

    function handlePlayClick() {
        // handle sentence overview click
        if (selected.type === 'sentences') {
            if (isPlaying) {
                stop();
                setTextToBeRead('');
                return setReadSentenceSequence(-1);
            }
            return setReadSentenceSequence(lastListenedIndex);
        }

        // handle single sentence or word click
        if (isPlaying) return stop();
        play();
    }

    function handleFinishExercise() {
        // 1. array of all highlight words: [{paragraphId: 1, sentenceId: 1, wordIndex: 2, word: 'houden'}]
        const wordsThatNeedHighlight = paragraphs
            .map((p) =>
                p.attributes.sentences
                    .map((sentence) =>
                        sentence.attributes.text
                            .split(' ')
                            .map((w, i) => {
                                const hasHighlighChar = w
                                    .split('')
                                    .find((letter) =>
                                        highlightChars.includes(letter as any)
                                    );

                                if (hasHighlighChar)
                                    return {
                                        paragraphId: p.key,
                                        sentenceId: sentence.key,
                                        wordIndex: i,
                                        word: w,
                                    };

                                return undefined;
                            })
                            .filter((exists) => exists)
                    )
                    .filter((arr) => !!arr.length)
                    .flat()
            )
            .flat();

        // 2. array of all items that weren't highlighted: ['houden', zouden, kou]
        const wordsThatWereNotHighlighted = wordsThatNeedHighlight
            .filter(
                (word) =>
                    !answeredSentences.find(
                        (answered) =>
                            answered.paragraphId === word?.paragraphId &&
                            answered.id === word?.sentenceId &&
                            answered.words.includes(word?.wordIndex)
                    )
            )
            .map((w) => w?.word);

        // 3. Loop over array and dispatch wrong_answer event for each
        wordsThatWereNotHighlighted.forEach((word) => {
            // dispatch wrong answer event
            dispatch(
                storeWrongAnswer({
                    given_answer: 'Niet gemarkeerd',
                    correct_answer: word,
                    type: 'highlight',
                    metadata: { sequence },
                    exercise_id: homeworkId,
                    exercise_type: 'homework',
                    treatment_id: treatmentId,
                })
            );
        });

        setStatus('finished');
    }

    useEffect(() => {
        setProgress(current + 1);

        if (current + 1 === paragraphs.length)
            return setExerciseStatus('finished');

        if (exerciseStatus === 'finished') setExerciseStatus('started');
    }, [current]);

    useEffect(() => setStatus(exerciseStatus), [exerciseStatus]);

    useEffect(() => {
        if (storedAnsweredSentences)
            setAnsweredSentences(storedAnsweredSentences);
    }, [!!storedAnsweredSentences]);

    useEffect(() => {
        // save user answer
        if (answeredSentences.length)
            dispatch(
                updateExerciseStatus({
                    exercise_id: exercise?.id,
                    exercise_type: 'homework',
                    finished: 0,
                    data: answeredSentences,
                })
            );
    }, [answeredSentences]);

    // reset reader when changing paragraph
    useEffect(() => {
        setLastListenedIndex(0);
        setTextToBeRead('');
        setReadSentenceSequence(-1);
        if (isPlaying) stop();
    }, [current]);

    useEffect(() => {
        if (selected.type === 'sentences' && lastListenedIndex > 0)
            setReadSentenceSequence(lastListenedIndex);
    }, [selected]);

    // reset readSentenceSequence when clicking on sentence
    useEffect(() => {
        if (readSentenceSequence > -1) setReadSentenceSequence(-1);
    }, [selected]);

    // Trigger play when playSequence is updated
    useEffect(() => {
        if (
            selected.type === 'sentences' &&
            textToBeRead &&
            readSentenceSequence > -1
        )
            play();
    }, [url]);

    // set read sequence to next or finished when play is finished
    useEffect(() => {
        if (!isPlaying && readSentenceSequence > -1) {
            const isLastSentence =
                readSentenceSequence === sentences.length - 1;

            // if last sentence, set readSentenceSequence status to finished
            if (isLastSentence) {
                setLastListenedIndex(0);
                return setReadSentenceSequence(-1);
            }

            // if not last sentence, set readSentenceSequence to next
            const currentReadSentence = readSentenceSequence + 1;
            setLastListenedIndex(currentReadSentence);
            setReadSentenceSequence(currentReadSentence);
        }
    }, [isPlaying]);

    const contextValues = useMemo(
        () => ({
            sentences,
            selected,
            setSelected,
            answeredSentences,
            setAnsweredSentences,
            paragraphId,
            textToBeRead,
            setTextToBeRead,
            readSentenceSequence,
            lastListenedIndex,
        }),
        [
            sentences,
            selected,
            answeredSentences,
            paragraphId,
            textToBeRead,
            setTextToBeRead,
            readSentenceSequence,
            lastListenedIndex,
        ]
    );

    return (
        <HighlightExerciseContext.Provider value={contextValues}>
            <ExerciseWrapper fullWidth>
                <div styleName="wrapper">
                    {sentences.map((s, i) => (
                        <Card index={i} key={`${i} ${current}`} />
                    ))}
                </div>
            </ExerciseWrapper>
            <ExerciseBottom
                controlBar={
                    <ControlBar
                        exerciseStatus={
                            // prevent bug where student can finish exercise before completing word highlighting
                            selected.type !== 'sentences'
                                ? 'started'
                                : exerciseStatus
                        }
                        next={selected.type === 'sentences' ? next : undefined}
                        overwriteFinished={handleFinishExercise}
                        textToBeRead={textToBeRead}
                        handlePlayClick={handlePlayClick}
                        isPaused={!isPlaying}
                        preventAutoPlay
                    />
                }
            />
        </HighlightExerciseContext.Provider>
    );
}
